import { Alert, Result, Spin } from 'antd';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { usePharmacyScope } from './pharmacy-scope';
import { useAuth } from './auth-context';
import type { Permission } from './permissions';

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spin size="large" />
    </div>
  );
}

export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) {
    // Carries where they were headed so sign-in can put them back there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

/**
 * Keeps a user off a page whose every request would 403. The server still
 * enforces this — the guard exists so the failure is a clear message instead of
 * a screen of red alerts.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { can } = useAuth();

  if (!can(permission)) {
    return (
      <Result
        status="403"
        title="Not permitted"
        subTitle={`This page needs the ${permission} permission.`}
      />
    );
  }
  return children;
}

/** Stock and listing routes are keyed by pharmacy; without one there is nothing to fetch. */
export function RequirePharmacyScope({ children }: { children: ReactNode }) {
  const { pharmacyId } = usePharmacyScope();

  if (!pharmacyId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Choose a pharmacy first"
        description="Stock and listings belong to a single pharmacy. Pick one from the selector in the header."
      />
    );
  }
  return children;
}
